import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/historypage.css';

// ADMIN: sees everyone's records and can filter by user. The user filter
// lives in the URL (?userId=...) so "View history" on the Users page can
// open this page already filtered.
// USER: the backend only ever returns their own records.
export default function HistoryPage() {
  const { admin, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = isAdmin ? searchParams.get('userId') || '' : '';

  const [records, setRecords] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ companyId: '', status: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get('/companies').then((res) => setCompanies(res.data.companies));
    if (isAdmin) api.get('/users').then((res) => setUsers(res.data.users));
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { ...filters, userId, page };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const res = await api.get('/offers', { params });
      setRecords(res.data.records);
      setPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters, page, userId]);

  const changeUser = (value) => {
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (value) next.set('userId', value);
    else next.delete('userId');
    setSearchParams(next);
  };

  const statusMeta = (status) => {
    if (status === 'SENT') return { className: 'hph-pill hph-pill--sent', icon: <CheckIcon /> };
    if (status === 'FAILED') return { className: 'hph-pill hph-pill--failed', icon: <XIcon /> };
    return { className: 'hph-pill hph-pill--downloaded', icon: <DownloadIcon /> };
  };

  return (
    <div className="hph-page">
      <div className="hph-topbar">
        <h1 className="hph-title">Offer Letter History</h1>
        {!loading && (
          <div className="hph-count-badge">{total} record{total === 1 ? '' : 's'}</div>
        )}
      </div>

      <div className="hph-filters">
        <div className="hph-search">
          <SearchIcon />
          <input
            className="hph-search-input"
            placeholder="Search candidate name or email…"
            value={filters.search}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, search: e.target.value })); }}
          />
        </div>

        <div className="hph-select-group">
          {isAdmin && (
            <select className="hph-select" value={userId} onChange={(e) => changeUser(e.target.value)}>
              <option value="">Everyone</option>
              <option value={admin?._id}>Only me</option>
              {users.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name}{u.isActive ? '' : ' (deactivated)'}
                </option>
              ))}
            </select>
          )}

          <select
            className="hph-select"
            value={filters.companyId}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, companyId: e.target.value })); }}
          >
            <option value="">All companies</option>
            {companies.map((c) => <option key={c._id} value={c._id}>{c.shortName}</option>)}
          </select>

          <select
            className="hph-select"
            value={filters.status}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, status: e.target.value })); }}
          >
            <option value="">All statuses</option>
            <option value="SENT">Sent</option>
            <option value="DOWNLOADED">Downloaded</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
      </div>

      <div className="hph-table-card">
        {loading ? (
          <div className="hph-state">
            <div className="hph-spinner" aria-hidden="true" />
            <p>Loading records…</p>
          </div>
        ) : records.length === 0 ? (
          <div className="hph-state">
            <InboxIcon />
            <p>No offer letters match these filters yet.</p>
          </div>
        ) : (
          <>
            <table className="hph-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Company</th>
                  <th>Job Title</th>
                  <th>Status</th>
                  {isAdmin && <th>Performed By</th>}
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const meta = statusMeta(r.status);
                  return (
                    <tr key={r._id}>
                      <td>
                        <div className="hph-candidate-name">{r.candidateName}</div>
                        <div className="hph-candidate-email">{r.candidateEmail}</div>
                      </td>
                      <td>
                        <div className="hph-company-cell">
                          <span className="hph-swatch" style={{ background: r.company?.accentColor }} />
                          {r.company?.shortName}
                        </div>
                      </td>
                      <td>{r.jobTitle}</td>
                      <td>
                        <span className={meta.className}>
                          {meta.icon}
                          {r.status}
                        </span>
                      </td>
                      {isAdmin && <td className="hph-muted">{r.performedBy?.name}</td>}
                      <td className="hph-muted">{new Date(r.createdAt).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="hph-pagination">
              <button
                type="button"
                className="hph-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeftIcon /> Prev
              </button>
              <span className="hph-page-label">Page {page} of {pages}</span>
              <button
                type="button"
                className="hph-page-btn"
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <ChevronRightIcon />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
      <line x1="20" y1="20" x2="16.2" y2="16.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 12.5 9.5 18 20 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3v12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 12h4l2 3h4l2-3h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="3" y="6" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}
